import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { DASHBOARD_STATE_TOKEN } from '~backoffice/app/~shared/constants/global.constants';
import {
  DashboardStateModel,
  GlobalStateModel
} from '~backoffice/app/~shared/models/store.models';
import {
  LoadAvailableDashboard,
  LoadDashboardEmbedGuestToken,
  RefreshDashboardEmbedGuestToken,
  ResetState,
  SelectDashboard,
  SetResourceTimeRange
} from '~backoffice/app/~store/actions/dashboard.actions';
import * as immutable from 'object-path-immutable';
import {
  catchError,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';
import { DashboardService } from '~core/services/dashboard.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { Pagination, SuperSetDashboard } from '~core/models/core.models';
import { sortBy } from 'lodash-es';
import { GetData } from '../actions/resource.actions';
import { GlobalState } from './global.state';
import { FreeRequestParams } from '~core/models/shared.models';
import { match } from 'ts-pattern';
import { PageName } from '~backoffice/app/~shared/models/shared.models';

export const DEFAULT_DASHBOARD_STATE: DashboardStateModel = {
  dashboardEntities: {
    data: [],
    loadingErrors: undefined,
    additionalData: undefined
  },
  selectedDashboard: undefined,
  dashboardGuestToken: {
    data: '',
    loadingErrors: undefined,
    additionalData: {
      latestRefreshedToken: undefined
    }
  },
  currentResourceTimeRange: undefined
};

@State({
  name: DASHBOARD_STATE_TOKEN,
  defaults: DEFAULT_DASHBOARD_STATE
})
@Injectable()
export class DashboardState {
  protected _dashboardService = inject(DashboardService);
  protected _permissionsService = inject(NgxPermissionsService);

  @Action(ResetState, { cancelUncompleted: true })
  resetState(ctx: StateContext<DashboardStateModel>, action: ResetState): void {
    ctx.setState({
      ...DEFAULT_DASHBOARD_STATE,
      ...action.data
    });
  }

  @Action(LoadAvailableDashboard)
  loadAvailableDashboard(
    ctx: StateContext<DashboardStateModel>
  ): void | Observable<unknown> {
    const state = ctx.getState();
    if (state.dashboardEntities.loadingErrors) {
      const wrap = immutable.wrap(state);
      wrap.set('dashboardEntities.loadingErrors', undefined);
      ctx.setState(wrap.value());
    }

    return this._dashboardService.listUserSuperSetDashboard().pipe(
      switchMap(res => {
        if (!res.items.length) return of(res);
        return forkJoin(
          res.items.map(el =>
            this._dashboardService.getEmbeddableSourceUuid(el.id).pipe(
              map(sourceEmbeddedUrl => ({ ...el, sourceEmbeddedUrl })),
              catchError(() => of({ ...el, sourceEmbeddedUrl: '' }))
            )
          )
        ).pipe(
          map(formattedDashboards =>
            sortBy(
              formattedDashboards.filter(el => !!el.sourceEmbeddedUrl),
              'order'
            )
          ),
          map(
            formattedDashboards =>
              ({
                ...res,
                items: formattedDashboards
              }) as Pagination<SuperSetDashboard>
          )
        );
      }),
      tap({
        next: ({ items, ...rest }) => {
          const wrap = immutable.wrap(ctx.getState());
          wrap.set('dashboardEntities.data', items);
          wrap.set('dashboardEntities.paginationData', rest);
          ctx.setState(wrap.value());

          // const permittedDashboards =
          //   DashboardService.pickPermittedDashboards(
          //     items,
          //     Object.keys(this._permissionsService.getPermissions())
          //   );

          // if (permittedDashboards.length) {
          //   ctx.dispatch([
          //     new SelectDashboard(permittedDashboards.at(0)!)
          //     // new LoadDashboardEmbedGuestToken()
          //   ]);
          // }
          ctx.dispatch([
            new SelectDashboard(items.at(0)!)
            // new LoadDashboardEmbedGuestToken()
          ]);
        },
        error: error => {
          const wrap = immutable.wrap(ctx.getState());
          wrap.set('dashboardEntities.loadingErrors', error);
          ctx.setState(wrap.value());
        }
      })
    );
  }

  @Action(SelectDashboard)
  selectDashboard(
    ctx: StateContext<DashboardStateModel>,
    action: SelectDashboard
  ): void | Observable<unknown> {
    ctx.patchState({ selectedDashboard: action.payload });
  }

  @Action(LoadDashboardEmbedGuestToken)
  loadDashboardEmbedGuestToken(
    ctx: StateContext<DashboardStateModel>
  ): void | Observable<unknown> {
    const state = ctx.getState();
    if (state.dashboardGuestToken.loadingErrors) {
      const wrap = immutable.wrap(state);
      wrap.set('dashboardGuestToken.loadingErrors', undefined);
      ctx.setState(wrap.value());
    }

    const { dashboardEntities } = state;
    const { data } = dashboardEntities;

    const permittedDashboards = DashboardService.pickPermittedDashboards(
      data,
      Object.keys(this._permissionsService.getPermissions())
    );

    if (!permittedDashboards.length) return;
    return this._dashboardService
      .getSuperSetGuestToken({
        resources: permittedDashboards.map(el => ({
          id: el.id,
          type: 'dashboard'
        }))
      })
      .pipe(
        tap({
          next: dashboardGuestToken => {
            const wrap = immutable.wrap(ctx.getState());
            wrap.set('dashboardGuestToken.data', dashboardGuestToken);
            ctx.setState(wrap.value());
          },
          error: error => {
            const wrap = immutable.wrap(ctx.getState());
            wrap.set('dashboardGuestToken.loadingErrors', error);
            ctx.setState(wrap.value());
          }
        })
      );
  }

  @Action(RefreshDashboardEmbedGuestToken)
  refreshDashboardEmbedGuestToken(
    ctx: StateContext<DashboardStateModel>
  ): void | Observable<unknown> {
    const state = ctx.getState();
    const { dashboardEntities } = state;
    const { data } = dashboardEntities;

    const permittedDashboards = DashboardService.pickPermittedDashboards(
      data,
      Object.keys(this._permissionsService.getPermissions())
    );

    if (!permittedDashboards.length) return;

    return this._dashboardService
      .getSuperSetGuestToken({
        resources: data.map(el => ({ id: el.id, type: 'dashboard' }))
      })
      .pipe(
        tap({
          next: dashboardGuestToken => {
            const wrap = immutable.wrap(ctx.getState());
            wrap.set(
              'dashboardGuestToken.additionalData.latestRefreshedToken',
              dashboardGuestToken
            );
            ctx.setState(wrap.value());
          }
        })
      );
  }

  @Action(SetResourceTimeRange)
  setResourceTimeRange(
    ctx: StateContext<DashboardStateModel>,
    action: SetResourceTimeRange
  ): void {
    ctx.patchState({ currentResourceTimeRange: action.payload });
    ctx.dispatch(new GetData({ refresh: true }));
  }

  // selectors

  @Selector()
  static dashboardList(
    state: DashboardStateModel
  ): Pick<
    DashboardStateModel,
    'dashboardEntities'
  >['dashboardEntities']['data'] {
    return state.dashboardEntities.data;
  }

  @Selector()
  static dashboardListPaginationData(
    state: DashboardStateModel
  ): Pick<
    DashboardStateModel,
    'dashboardEntities'
  >['dashboardEntities']['additionalData'] {
    return state.dashboardEntities.additionalData;
  }

  @Selector()
  static dashboardListErrors(
    state: DashboardStateModel
  ): Pick<
    DashboardStateModel,
    'dashboardEntities'
  >['dashboardEntities']['loadingErrors'] {
    return state.dashboardEntities.loadingErrors;
  }

  @Selector()
  static selectedDashboard(
    state: DashboardStateModel
  ): Pick<DashboardStateModel, 'selectedDashboard'>['selectedDashboard'] {
    return state.selectedDashboard;
  }

  @Selector()
  static dashboardGuestToken(
    state: DashboardStateModel
  ): Pick<
    DashboardStateModel,
    'dashboardGuestToken'
  >['dashboardGuestToken']['data'] {
    return state.dashboardGuestToken.data;
  }

  @Selector()
  static dashboardGuestTokenLoadingError(
    state: DashboardStateModel
  ): Pick<
    DashboardStateModel,
    'dashboardGuestToken'
  >['dashboardGuestToken']['loadingErrors'] {
    return state.dashboardGuestToken.loadingErrors;
  }

  @Selector()
  static dashboardLatestRefreshedGuestToken(
    state: DashboardStateModel
  ): string | null | undefined {
    return state.dashboardGuestToken.additionalData?.latestRefreshedToken;
  }

  @Selector()
  static currentResourceTimeRange(
    state: DashboardStateModel
  ): DashboardStateModel['currentResourceTimeRange'] {
    return state.currentResourceTimeRange;
  }

  @Selector([DashboardState, GlobalState.routeData])
  static currentResourceTimeRangeQuery(
    state: DashboardStateModel,
    routeData: Pick<GlobalStateModel, 'routeData'>['routeData']
  ): FreeRequestParams {
    if (!state.currentResourceTimeRange) {
      return {};
    }

    return match({
      currentResourceTimeRange: state.currentResourceTimeRange,
      pageName: routeData?.data?.pageName as PageName | undefined
    })
      .when(
        ({ pageName }) =>
          pageName && [PageName.taxProfileOrganizations].includes(pageName),
        ({ currentResourceTimeRange }) => ({
          startDateTime: currentResourceTimeRange.startDateTime,
          endDateTime: currentResourceTimeRange.endDateTime
        })
      )
      .otherwise(({ currentResourceTimeRange }) => ({
        createdAt: `range[${currentResourceTimeRange.startDateTime},${currentResourceTimeRange.endDateTime}]`
      }));
  }
}
